import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CHAIN_IMAGES } from '../config/chainImages';
import BridgeApiService from '../services/bridgeApi';
import SignalSnippet from '../components/Signal/SignalSnippet';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    treasuryValue: 0,
    totalDisbursed: 0,
    netGrowth: 0,
    holders: 5714,
  });
  const [totalBridged, setTotalBridged] = useState({ displayAmount: '$0', totalTransactions: 0 });

  useEffect(() => {
    document.title = 'Velvet AI - Automated Trading DAO';
    fetchStats();
    fetchBridgeStats();
  }, []);

  const fetchBridgeStats = async () => {
    const result = await BridgeApiService.getTotalBridged();
    if (result.success) {
      setTotalBridged(result.data);
    }
  };

  const fetchStats = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const [treasuryResponse, payoutsResponse] = await Promise.all([
        axios.get(`${apiUrl}/portfolio/treasury-balance`),
        axios.get(`${apiUrl}/portfolio/payouts`)
      ]);

      const currentBalance = treasuryResponse.data?.currentBalance || 0;
      const avaxPrice = treasuryResponse.data?.avaxPrice || payoutsResponse.data?.currentAVAXPrice || 42;
      const totalDistributed = payoutsResponse.data?.totalAVAXDistributed || 0;
      const startingBalance = 750;
      const totalAVAXEarned = totalDistributed + (currentBalance - startingBalance);
      const netGrowthPercentage = ((totalAVAXEarned / startingBalance) * 100);

      setStats({
        treasuryValue: currentBalance * avaxPrice,
        totalDisbursed: totalDistributed * avaxPrice,
        netGrowth: netGrowthPercentage,
        holders: 5714,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const supportedChains = [
    { id: 1, name: 'Ethereum' },
    { id: 10, name: 'Optimism' },
    { id: 137, name: 'Polygon' },
    { id: 8453, name: 'Base' },
    { id: 42161, name: 'Arbitrum' },
    { id: 43114, name: 'Avalanche' },
    { id: 59144, name: 'Linea' },
    { id: 146, name: 'Sonic' },
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toFixed(0)}`;
  };

  return (
    <div className="home-minimal">
      {/* Hero */}
      <section className="hero-minimal">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="hero-title-min">
            The Future of<br />
            <span className="text-gradient">DeFi Trading</span>
          </h1>
          <p className="hero-desc-min">
            AI-powered market analysis, seamless bridging, and automated trading strategies.
          </p>
          <div className="hero-btns">
            <button className="btn-main" onClick={() => navigate('/signal')}>
              View AI Signals
              <FaArrowRight />
            </button>
            <button className="btn-outline" onClick={() => navigate('/bridge')}>
              Bridge USDC
            </button>
          </div>
        </motion.div>
      </section>

      {/* AI Signal */}
      <section className="section-min">
        <div className="section-header-min">
          <span className="section-tag">Live Analysis</span>
          <h2 className="section-title-min">AI Market Signals</h2>
          <p className="section-desc-min">
            Real-time trading signals powered by multi-timeframe technical analysis
          </p>
        </div>
        <SignalSnippet />
      </section>

      {/* Bridge */}
      <section className="section-min">
        <div className="section-header-min">
          <span className="section-tag">Cross-Chain</span>
          <h2 className="section-title-min">USDC Bridge</h2>
          <p className="section-desc-min">
            The cheapest way to bridge USDC across chains. Free transfers to Avalanche.
          </p>
        </div>

        <div className="stats-row">
          <div className="stat-item-min">
            <span className="stat-value-min">{totalBridged.displayAmount}</span>
            <span className="stat-label-min">Total Bridged</span>
          </div>
          <div className="stat-item-min">
            <span className="stat-value-min">{totalBridged.totalTransactions.toLocaleString()}</span>
            <span className="stat-label-min">Transactions</span>
          </div>
          <div className="stat-item-min highlight">
            <span className="stat-value-min">$0</span>
            <span className="stat-label-min">Fee to Avalanche</span>
          </div>
        </div>

        <div className="chains-row">
          {supportedChains.map((chain) => (
            <div key={chain.id} className="chain-item-min">
              <img src={CHAIN_IMAGES[chain.id]} alt={chain.name} />
              <span>{chain.name}</span>
            </div>
          ))}
        </div>

        <button className="btn-outline full-width" onClick={() => navigate('/bridge')}>
          Start Bridging
          <FaArrowRight />
        </button>
      </section>

      {/* Trading Bot */}
      <section className="section-min">
        <div className="section-header-min">
          <span className="section-tag">Automated</span>
          <h2 className="section-title-min">AI Trading Bot</h2>
          <p className="section-desc-min">
            Automated trading with 100% revenue sharing to VELAI holders
          </p>
        </div>

        <div className="stats-row">
          <div className="stat-item-min">
            <span className="stat-value-min">{formatNumber(stats.treasuryValue)}</span>
            <span className="stat-label-min">Treasury Value</span>
          </div>
          <div className="stat-item-min">
            <span className="stat-value-min">{formatNumber(stats.totalDisbursed)}</span>
            <span className="stat-label-min">Distributed</span>
          </div>
          <div className="stat-item-min highlight">
            <span className="stat-value-min">+{stats.netGrowth.toFixed(1)}%</span>
            <span className="stat-label-min">Net Growth</span>
          </div>
          <div className="stat-item-min">
            <span className="stat-value-min">{stats.holders.toLocaleString()}</span>
            <span className="stat-label-min">Holders</span>
          </div>
        </div>

        <div className="info-banner">
          <div>
            <strong>100% Revenue Share</strong>
            <span>All trading profits go to VELAI stakers</span>
          </div>
          <button className="btn-main small" onClick={() => navigate('/staking')}>
            Stake Now
          </button>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-min">
        <h2>Ready to get started?</h2>
        <p>Join the Velvet AI ecosystem today</p>
        <div className="cta-btns">
          <button className="btn-main" onClick={() => navigate('/swap')}>
            Buy VELAI
          </button>
          <button className="btn-outline" onClick={() => navigate('/dashboard')}>
            View Dashboard
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
